import { Service, computed, signal } from '@angular/core';
import type { ICliente, IPermiso, IToken, IUsuario } from 'modelos';

import { environment } from '@env';

/**
 * Claves de `sessionStorage`.
 *
 * Son **las mismas que IRIX** a propósito: el traspaso de sesión entre pestañas
 * copia el `sessionStorage` entero, así que los nombres tienen que coincidir o
 * el handoff deja la sesión a medias.
 */
export const CLAVES = {
  accessToken: 'accessToken',
  refreshToken: 'refreshToken',
  usuario: 'usuario',
  permisosUsuario: 'permisosUsuario',
  permisoActual: 'permisoActual',
  idPermiso: 'x-permiso',
  cliente: 'cliente',
  clientePropio: 'clientePropio',
} as const;

/**
 * `sessionStorage` puede tirar en modo privado o con el almacenamiento
 * bloqueado. Ninguna lectura ni escritura puede tumbar la aplicación.
 */
function leerTexto(clave: string): string {
  try {
    return sessionStorage.getItem(clave) ?? '';
  } catch {
    return '';
  }
}

function leerJson<T>(clave: string): T | undefined {
  const crudo = leerTexto(clave);
  if (!crudo) return undefined;
  try {
    return JSON.parse(crudo) as T;
  } catch {
    return undefined;
  }
}

function escribir(clave: string, valor: string | undefined): void {
  try {
    if (valor === undefined || valor === '') sessionStorage.removeItem(clave);
    else sessionStorage.setItem(clave, valor);
  } catch {
    /* almacenamiento no disponible: la sesión vive sólo en memoria */
  }
}

function escribirJson(clave: string, valor: unknown): void {
  escribir(clave, valor === undefined ? undefined : JSON.stringify(valor));
}

/** Propia de FARO, fuera de `CLAVES`: IRIX no la escribe ni la copia. */
const CLAVE_ENTORNO = 'faroEntornoSesion';

/**
 * Descarta la sesión guardada si es de **otro entorno**.
 *
 * El `sessionStorage` sobrevive a la recarga que hace `ng serve` al cambiar
 * `env` en `environment.ts`: sin esto, la pestaña sigue mandándole a prod el
 * token y el `x-permiso` de test. El permiso no existe en la otra base y los
 * listados vuelven vacíos, sin ningún error que lo explique.
 *
 * Sin marca se toma como válida y se marca: es la sesión que llega del traspaso
 * desde IRIX, que copia sólo `CLAVES`.
 */
function descartarSesionDeOtroEntorno(): void {
  const guardado = leerTexto(CLAVE_ENTORNO);
  if (guardado && guardado !== environment.nombre) {
    for (const clave of Object.values(CLAVES)) escribir(clave, undefined);
  }
  escribir(CLAVE_ENTORNO, environment.nombre);
}

/**
 * Estado de la sesión, como signals, respaldado por `sessionStorage`.
 *
 * Es deliberadamente **tonto**: guarda y expone. Quien habla con el backend es
 * `SesionService`; quien decide si alcanza para entrar a una ruta son los
 * guards. Así el interceptor de autenticación puede leer el token sin arrastrar
 * el servicio que hace login (que a su vez usa HttpClient).
 */
@Service()
export class SesionStore {
  // El descarte va en el primer inicializador porque el resto de los campos lee
  // el `sessionStorage` al construirse, antes que cualquier constructor.
  private readonly _accessToken = signal(
    (descartarSesionDeOtroEntorno(), leerTexto(CLAVES.accessToken)),
  );
  private readonly _refreshToken = signal(leerTexto(CLAVES.refreshToken));
  private readonly _usuario = signal(leerJson<IUsuario>(CLAVES.usuario));
  private readonly _permisos = signal(leerJson<IPermiso[]>(CLAVES.permisosUsuario) ?? []);
  private readonly _permisoActual = signal(leerJson<IPermiso>(CLAVES.permisoActual));
  private readonly _cliente = signal(leerJson<ICliente>(CLAVES.cliente));
  private readonly _clientePropio = signal(leerJson<ICliente>(CLAVES.clientePropio));

  readonly accessToken = this._accessToken.asReadonly();
  readonly refreshToken = this._refreshToken.asReadonly();
  readonly usuario = this._usuario.asReadonly();
  readonly permisos = this._permisos.asReadonly();
  readonly permisoActual = this._permisoActual.asReadonly();
  readonly cliente = this._cliente.asReadonly();
  readonly clientePropio = this._clientePropio.asReadonly();

  readonly logueado = computed(() => this._accessToken().length > 0);

  /** Viaja como header `x-permiso` en cada request. */
  readonly idPermiso = computed(() => this._permisoActual()?._id ?? '');

  readonly modulos = computed(() => this._permisoActual()?.modulos);

  /** La única bandera de módulo que decide si FARO sirve para este permiso. */
  readonly tieneAlumbrado = computed(() => this.modulos()?.moduloLuminarias === true);

  /** Habilita la sección Mantenimiento (servicio técnico). Etapa 2. */
  readonly tieneEventosTecnicos = computed(() => this.modulos()?.moduloEventosTecnicos === true);

  /**
   * Habilita la sección Administración: clientes, permisos y roles.
   *
   * Es la misma bandera que en IRIX. Acá abre menos cosas —no hay ABM de
   * trackers ni de apikeys— pero decide lo mismo: si esta persona reparte
   * accesos o sólo los usa.
   */
  readonly tieneAdministracion = computed(() => this.modulos()?.moduloAdministracion === true);

  /** Habilita las vistas de logs crudos dentro de Red LoRaWAN. */
  readonly tieneLogs = computed(() => this.modulos()?.moduloLogs === true);

  /** Integración Montevideo: habilita Puestas y Grupos de puestas. */
  readonly usaPuestas = computed(
    () => this._clientePropio()?.config?.moduloLuminarias?.usaPuestas === true,
  );

  readonly hayAlgunPermisoActivo = computed(() => this._permisos().some((p) => !!p.activo));

  // ── Escritura ──────────────────────────────────────────────────────────────

  guardarTokens(token: IToken): void {
    this._accessToken.set(token.accessToken ?? '');
    this._refreshToken.set(token.refreshToken ?? '');
    escribir(CLAVES.accessToken, token.accessToken);
    escribir(CLAVES.refreshToken, token.refreshToken);

    if (token.user) this.setUsuario(token.user);
  }

  setUsuario(usuario: IUsuario): void {
    this._usuario.set(usuario);
    escribirJson(CLAVES.usuario, usuario);
  }

  setPermisos(permisos: IPermiso[] | undefined): void {
    this._permisos.set(permisos ?? []);
    escribirJson(CLAVES.permisosUsuario, permisos ?? []);
  }

  setPermisoActual(permiso: IPermiso | undefined): void {
    this._permisoActual.set(permiso);
    escribirJson(CLAVES.permisoActual, permiso);
    escribir(CLAVES.idPermiso, permiso?._id);
  }

  setCliente(cliente: ICliente | undefined): void {
    this._cliente.set(cliente);
    escribirJson(CLAVES.cliente, cliente);
  }

  setClientePropio(cliente: ICliente | undefined): void {
    this._clientePropio.set(cliente);
    escribirJson(CLAVES.clientePropio, cliente);
  }

  /** Relee todo desde `sessionStorage`. Lo usa el handoff entre pestañas. */
  recargarDesdeAlmacenamiento(): void {
    this._accessToken.set(leerTexto(CLAVES.accessToken));
    this._refreshToken.set(leerTexto(CLAVES.refreshToken));
    this._usuario.set(leerJson<IUsuario>(CLAVES.usuario));
    this._permisos.set(leerJson<IPermiso[]>(CLAVES.permisosUsuario) ?? []);
    this._permisoActual.set(leerJson<IPermiso>(CLAVES.permisoActual));
    this._cliente.set(leerJson<ICliente>(CLAVES.cliente));
    this._clientePropio.set(leerJson<ICliente>(CLAVES.clientePropio));
  }

  limpiar(): void {
    try {
      sessionStorage.clear();
    } catch {
      /* ignorado */
    }
    this._accessToken.set('');
    this._refreshToken.set('');
    this._usuario.set(undefined);
    this._permisos.set([]);
    this._permisoActual.set(undefined);
    this._cliente.set(undefined);
    this._clientePropio.set(undefined);
  }
}
